import React, { useState } from 'react';
import API from '../../api';
import { toast } from 'react-toastify';
import { 
  Send, 
  Bell, 
  Users, 
  Shield, 
  Store, 
  Type, 
  FileText, 
  Info,
  Loader2
} from 'lucide-react';

export default function AdminSendNotification() {
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    targetRole: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.message) {
      toast.error('Please enter a title and message');
      return;
    }

    if (!window.confirm('Are you sure you want to send this broadcast?')) return;

    setLoading(true);
    try {
      const response = await API.post('/notifications/broadcast', formData);
      if (response.data.success) {
        toast.success(response.data.message);
        setFormData({ title: '', message: '', targetRole: '' });
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || 'Failed to send notifications');
    }
    setLoading(false);
  };

  const audienceOptions = [
    { value: '', label: 'All Users', icon: Users, desc: 'Everyone registered' },
    { value: 'user', label: 'Pharmacies', icon: Store, desc: 'Pharmacy accounts only' },
    { value: 'admin', label: 'Admins', icon: Shield, desc: 'Internal staff only' },
  ];

  return (
    <>
      <style>{`
        /* --- Variables & Reset --- */
        :root {
          --primary-color: #2563eb;
          --primary-hover: #1d4ed8;
          --bg-color: #f8fafc;
          --card-bg: #ffffff;
          --text-dark: #1e293b;
          --text-gray: #64748b;
          --border-color: #e2e8f0;
          --focus-ring: rgba(37, 99, 235, 0.2);
          --shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
        }

        .admin-notify-page {
          background-color: var(--bg-color);
          min-height: 100vh;
          padding: 2rem;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
          box-sizing: border-box;
        }

        .admin-notify-container {
          max-width: 1100px;
          margin: 0 auto;
        }

        /* --- Header --- */
        .admin-notify-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 2.5rem;
        }

        .header-icon-box {
          background-color: var(--primary-color);
          padding: 0.75rem;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .header-icon {
          color: white;
          width: 32px;
          height: 32px;
        }

        .page-title {
          font-size: 1.875rem;
          font-weight: 700;
          color: var(--text-dark);
          margin: 0;
        }

        .page-subtitle {
          color: var(--text-gray);
          margin-top: 0.5rem;
          font-size: 1rem;
        }

        /* --- Main Layout Grid --- */
        .admin-notify-layout {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 2rem;
        }

        /* --- Card Styles --- */
        .notify-form-card, 
        .preview-card {
          background-color: var(--card-bg);
          border-radius: 16px;
          box-shadow: var(--shadow-lg);
          border: 1px solid var(--border-color);
          padding: 2rem;
        }

        /* --- Audience Selector --- */
        .form-section {
          margin-bottom: 1.5rem;
        }

        .input-label {
          display: block;
          font-size: 0.875rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .audience-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
        }

        .audience-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          background: #fff;
          border: 2px solid var(--border-color);
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: center;
        }

        .audience-card:hover {
          border-color: #cbd5e1;
        }

        .audience-card.active {
          border-color: var(--primary-color);
          background-color: #eff6ff;
        }

        .audience-card.active .audience-icon,
        .audience-card.active .audience-label {
          color: var(--primary-color);
        }

        .audience-icon {
          width: 24px;
          height: 24px;
          color: #94a3b8;
          margin-bottom: 0.5rem;
        }

        .audience-label {
          font-weight: 600;
          font-size: 0.9rem;
          color: var(--text-dark);
        }

        .audience-desc {
          font-size: 0.75rem;
          color: var(--text-gray);
          margin-top: 4px;
          line-height: 1.2;
        }

        /* --- Form Inputs --- */
        .form-group {
          margin-bottom: 1.5rem;
        }

        .input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .input-wrapper.textarea-wrapper {
          align-items: flex-start;
        }

        .input-icon {
          position: absolute;
          left: 1rem;
          width: 20px;
          height: 20px;
          color: #94a3b8;
          pointer-events: none;
        }

        .textarea-wrapper .input-icon {
          top: 1rem;
        }

        .form-input {
          width: 100%;
          padding: 0.875rem 1rem 0.875rem 2.75rem;
          font-size: 1rem;
          border: 1px solid var(--border-color);
          border-radius: 10px;
          background-color: #f8fafc;
          color: var(--text-dark);
          transition: all 0.2s;
          outline: none;
          box-sizing: border-box;
        }

        .form-input:focus {
          background-color: #fff;
          border-color: var(--primary-color);
          box-shadow: 0 0 0 3px var(--focus-ring);
        }

        .form-input::placeholder {
          color: #cbd5e1;
        }

        .form-textarea {
          resize: none;
          font-family: inherit;
        }

        /* --- Submit Button --- */
        .submit-btn {
          width: 100%;
          padding: 1rem;
          background: linear-gradient(to right, var(--primary-color), #4f46e5);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 1.1rem;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          box-shadow: 0 4px 6px rgba(37, 99, 235, 0.3);
          transition: transform 0.1s, box-shadow 0.2s;
        }

        .submit-btn:hover {
          background: linear-gradient(to right, #1d4ed8, #4338ca);
          box-shadow: 0 6px 12px rgba(37, 99, 235, 0.4);
        }

        .submit-btn:active {
          transform: scale(0.98);
        }

        .submit-btn:disabled {
          background: #cbd5e1;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        .spinner {
          animation: spin 1s linear infinite;
          width: 20px;
          height: 20px;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* --- Preview Sidebar --- */
        .preview-heading {
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: #94a3b8;
          margin-bottom: 1rem;
        }

        .mock-notification {
          background: white;
          border-left: 4px solid var(--primary-color);
          border-radius: 0 8px 8px 0;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          padding: 1rem;
          margin-bottom: 1.5rem;
        }

        .mock-title {
          font-size: 1rem;
          font-weight: 700;
          margin: 0 0 0.25rem 0;
          color: var(--text-dark);
        }

        .mock-message {
          font-size: 0.9rem;
          color: #475569;
          margin: 0;
          line-height: 1.4;
          word-wrap: break-word;
        }

        .mock-title.placeholder,
        .mock-message.placeholder {
          color: #cbd5e1;
          font-style: italic;
        }

        .mock-time {
          display: block;
          font-size: 0.75rem;
          color: #94a3b8;
          margin-top: 0.75rem;
        }

        .info-box {
          background-color: #eff6ff;
          border-radius: 8px;
          padding: 1rem;
          display: flex;
          gap: 0.75rem;
          align-items: flex-start;
        }

        .info-icon {
          color: var(--primary-color);
          min-width: 20px;
          margin-top: 2px;
        }

        .info-text {
          font-size: 0.85rem;
          color: #1e40af;
          margin: 0;
          line-height: 1.5;
        }

        /* --- Responsive Design --- */
        @media (max-width: 900px) {
          .admin-notify-layout {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 600px) {
          .admin-notify-page {
            padding: 1rem;
          }
          .audience-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="admin-notify-page">
        <div className="admin-notify-container">
          
          {/* Header Section */}
          <div className="admin-notify-header">
            <div className="header-icon-box">
              <Bell className="header-icon" />
            </div>
            <div>
              <h1 className="page-title">Broadcast Notification</h1>
              <p className="page-subtitle">
                Send system-wide alerts, updates, or maintenance messages to your users.
              </p>
            </div>
          </div>

          <div className="admin-notify-layout">
            
            {/* Left Column: Form */}
            <div className="notify-form-card">
              <form onSubmit={handleSubmit}>
                
                {/* Target Audience Selector */}
                <div className="form-section">
                  <label className="input-label">Target Audience</label>
                  <div className="audience-grid">
                    {audienceOptions.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, targetRole: opt.value })}
                        className={`audience-card ${formData.targetRole === opt.value ? 'active' : ''}`}
                      >
                        <opt.icon className="audience-icon" />
                        <span className="audience-label">{opt.label}</span>
                        <span className="audience-desc">{opt.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Title Input */}
                <div className="form-group">
                  <label className="input-label">Notification Title</label>
                  <div className="input-wrapper">
                    <Type className="input-icon" />
                    <input
                      type="text"
                      placeholder="e.g. Scheduled Maintenance"
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      className="form-input"
                    />
                  </div>
                </div>

                {/* Message Input */}
                <div className="form-group">
                  <label className="input-label">Message Content</label>
                  <div className="input-wrapper textarea-wrapper">
                    <FileText className="input-icon" />
                    <textarea
                      rows="5"
                      placeholder="Type your detailed message here..."
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="form-input form-textarea"
                    ></textarea>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="spinner" /> Sending...
                    </>
                  ) : (
                    <>
                      <Send className="btn-icon" /> Send Broadcast
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Right Column: Preview */}
            <div className="notify-preview-sidebar">
              <div className="preview-card">
                <h3 className="preview-heading">User Preview</h3>
                
                {/* The Mock Notification Card */}
                <div className="mock-notification">
                  <div className="mock-content">
                    <h4 className={formData.title ? 'mock-title' : 'mock-title placeholder'}>
                      {formData.title || 'Title will appear here'}
                    </h4>
                    <p className={formData.message ? 'mock-message' : 'mock-message placeholder'}>
                      {formData.message || 'Message content will appear here...'}
                    </p>
                    <span className="mock-time">Just now</span>
                  </div>
                </div>

                {/* Info Box */}
                <div className="info-box">
                  <Info className="info-icon" />
                  <p className="info-text">
                    <strong>Note:</strong> Broadcasts are sent immediately. 
                    {formData.targetRole === '' 
                      ? " This will reach ALL registered users." 
                      : ` This will only reach ${formData.targetRole === 'user' ? 'pharmacy accounts' : 'admin accounts'}.`}
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}